import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setDispatchSuggestedOrganization } from '~/src/redux/lead/lead.slice';
import { selectIsShowAddressRequiredPlank, selectSuggestedOrganization } from '~/src/redux/lead/lead.selectors';
import { useClientRequestForm } from '~/src/context/client-request-form/use-client-request-form.hook';
import { useInternetTechAbility } from '~/src/hooks/use-internet-tech-ability.hook';
import { useHouseFias } from '~/src/hooks/use-house-fias.hook';
import { LegalInfo } from '~/src/components/common-components/legal-info/legal-info';
import { DropDownOrganizationField } from '~/src/components/common-components/organization-field/organization-field';
import { AddressRequiredPlank } from '~/src/components/common-components/address-required-plank/address-required-plank';
import { TechAbilityPlank } from '~/src/components/common-components/tech-ability-plank/tech-ability-plank';
import { OrganizationInfoSubDataInputs } from '~/src/components/blocks/referral-products/organization-info-ref/components/organization-info-sub-data-inputs/organization-info-sub-data-inputs';
import { onSendOrganizationInfoAnalytics } from '~/src/components/blocks/referral-products/organization-info-ref/organization-info.analytics';
import { PersonContacts } from '~/src/components/blocks/referral-products/organization-info-ref/components/person-contacts/person-contacts';
import { AddressInfoBlock } from '~/src/components/blocks/referral-products/organization-info-ref/components/address-info-block/address-info-block';
import { SelfEmployedCheckbox } from '~/src/components/blocks/clients-request/organization-info/components/self-employed-checkbox/self-employed-checkbox';
import checkFieldsSuccessFillForAnalytics from '~/src/helpers/analytics-ref/analytics-ref.helper';
import { SectionContainerLeadPage, TitleLeadPage } from '~/src/styles/shared-css/common-lead.styles';
import { ATTR_NAMES_LEAD } from '~/src/models/constants/attr-names-lead.constants';
import { CHECKBOX_LABEL } from '~/src/components/blocks/clients-request/organization-info/organization-info.constants';
import {
    DROPDOWN_LABEL,
    DROPDOWN_PLACEHOLDER,
    INITIAL_CREATE_LEAD_DATA_ORGANIZATION,
} from '~/src/components/blocks/referral-products/organization-info-ref/organization-info.constants';
import { OrganizationInfoRefProps } from '~/src/components/blocks/referral-products/organization-info-ref/organization-info.types';
import type { DataSuccessIconType } from '~/src/helpers/analytics-ref/analytics-ref.helper.types';
import type { CustomInputChangeInterface } from '~/src/components/form-components/dropdown/dropdown.types';
import * as Styled from './organization-info.styles';

type UseRefType = DataSuccessIconType<string>;

export const OrganizationInfoRef: FC<OrganizationInfoRefProps> = ({
    title,
    dropDownOrganizationErrorRef,
    innErrorRef,
    ogrnErrorRef,
    selfEmployedFioErrorRef,
    organizationIsRequired,
    withAutoSelectRegion,
    regionsData,
    regionErrorRef,
    addressErrorRef,
    inputErrorRef,
    emailErrorRef,
    eventCategory,
    contactFioLabel,
    hasMtsInternetProduct,
    isInternetSelected,
    onAddressValidationChange,
    onOrganizationFieldValidationChange,
    onHouseFiasIdChange,
}) => {
    const dispatch = useDispatch();
    const [isClear, setIsClear] = useState(false);
    const [houseFiasId, setHouseFiasId] = useState('');
    const houseFiasIdRef = useRef('');
    houseFiasIdRef.current = houseFiasId;
    const getHouseFiasId = useCallback(() => houseFiasIdRef.current, []);

    const isShowAddressRequiredPlank = useSelector(selectIsShowAddressRequiredPlank);
    const suggestedOrganization = useSelector(selectSuggestedOrganization);
    const organizationSuccessIconForAnalyticsRef = useRef<UseRefType>(INITIAL_CREATE_LEAD_DATA_ORGANIZATION);
    const organizationErrorIconForAnalyticsRef = useRef<UseRefType>(INITIAL_CREATE_LEAD_DATA_ORGANIZATION);
    const organizationIsSelected = !!Object.keys(suggestedOrganization)?.length;
    const { state } = useClientRequestForm();

    const { data, isLoading, isError, fetchTechAbility, reset, shouldShowPlank } = useInternetTechAbility(
        isInternetSelected,
        getHouseFiasId,
    );

    const { handleSelectAddress, handleClearAddress, handleAddressValueChange } = useHouseFias(
        houseFiasId,
        setHouseFiasId,
        isInternetSelected,
        fetchTechAbility,
        reset,
    );

    useEffect(() => {
        organizationIsSelected && setIsClear(false);
        organizationIsSelected && onSendOrganizationInfoAnalytics('organizaciya', eventCategory);
    }, [suggestedOrganization]);

    useEffect(() => {
        onHouseFiasIdChange(houseFiasId);
    }, [houseFiasId]);

    const handleCloseLegalInfo = () => {
        setIsClear(true);
    };

    const handleClickServiceProvisionAddress = (suggestedData: CustomInputChangeInterface) => {
        handleSelectAddress(suggestedData?.houseFiasId);

        if (suggestedData?.suggestValue) {
            onSendOrganizationInfoAnalytics('adres_predostavleniya_uslugi', eventCategory);
        }
    };

    return (
        <SectionContainerLeadPage>
            <TitleLeadPage>{title || '2. Сведения о вашей организации'}</TitleLeadPage>
            <Styled.OrganizationInfoWrapper>
                <Styled.OrganizationDropdownContainer>
                    {hasMtsInternetProduct && (
                        <SelfEmployedCheckbox name={ATTR_NAMES_LEAD.selfEmployed} labelText={CHECKBOX_LABEL} />
                    )}
                    {state.currentVariant === 'default' && (
                        <>
                            <Styled.OrganizationDropdownBlock>
                                <DropDownOrganizationField
                                    name={ATTR_NAMES_LEAD.legalName}
                                    enableError={organizationIsRequired}
                                    label={DROPDOWN_LABEL}
                                    placeholder={DROPDOWN_PLACEHOLDER}
                                    required={organizationIsRequired}
                                    withoutSuggest={false}
                                    inputErrorRef={dropDownOrganizationErrorRef}
                                    setDispatchSuggestedOrganization={(args) =>
                                        dispatch(setDispatchSuggestedOrganization(args || suggestedOrganization))
                                    }
                                    noReset
                                    isSearchIcon={true}
                                    hasNeedClear={isClear}
                                    isHideCloseIcon={true}
                                    autocompleteWithSelect
                                    initialSuggestedOrganization={suggestedOrganization}
                                    value={suggestedOrganization?.name?.fullName}
                                    analyticsref={checkFieldsSuccessFillForAnalytics.bind({
                                        filedName: ATTR_NAMES_LEAD.legalName,
                                        dataSuccessRef: organizationSuccessIconForAnalyticsRef,
                                        sendAnalytics: () =>
                                            onSendOrganizationInfoAnalytics('organizaciya', eventCategory),
                                    })}
                                    analyticsreferror={checkFieldsSuccessFillForAnalytics.bind({
                                        filedName: ATTR_NAMES_LEAD.legalName,
                                        dataSuccessRef: organizationErrorIconForAnalyticsRef,
                                        sendAnalytics: () =>
                                            onSendOrganizationInfoAnalytics(
                                                'organizaciya',
                                                eventCategory,
                                                'Выберите организацию из списка',
                                            ),
                                    })}
                                    onOrganizationFieldValidationChange={onOrganizationFieldValidationChange}
                                />
                                {organizationIsSelected && (
                                    <Styled.OrganizationDropdownSubBlock>
                                        <LegalInfo
                                            suggestedOrganization={suggestedOrganization}
                                            onClose={handleCloseLegalInfo}
                                        />
                                    </Styled.OrganizationDropdownSubBlock>
                                )}
                            </Styled.OrganizationDropdownBlock>
                            <OrganizationInfoSubDataInputs
                                ogrnErrorRef={ogrnErrorRef}
                                innErrorRef={innErrorRef}
                                eventCategory={eventCategory}
                            />
                        </>
                    )}
                    <Styled.PersonContactsContainer>
                        <PersonContacts
                            inputErrorRef={inputErrorRef}
                            emailErrorRef={emailErrorRef}
                            innErrorRef={innErrorRef}
                            selfEmployedFioErrorRef={selfEmployedFioErrorRef}
                            eventCategory={eventCategory}
                            contactFioLabel={contactFioLabel}
                        />
                    </Styled.PersonContactsContainer>
                    <AddressInfoBlock
                        regionsData={regionsData}
                        regionErrorRef={regionErrorRef}
                        addressErrorRef={addressErrorRef}
                        withAutoSelectRegion={withAutoSelectRegion}
                        eventCategory={eventCategory}
                        onAddressValidationChange={onAddressValidationChange}
                        onClickServiceProvisionAddress={handleClickServiceProvisionAddress}
                        onClearAddress={handleClearAddress}
                        onAddressValueChange={handleAddressValueChange}
                    />
                </Styled.OrganizationDropdownContainer>
            </Styled.OrganizationInfoWrapper>
            {isShowAddressRequiredPlank && !shouldShowPlank && <AddressRequiredPlank />}
            {isInternetSelected && shouldShowPlank && (
                <TechAbilityPlank
                    isError={isError}
                    isLoading={isLoading}
                    techAbility={data?.techAbility}
                    onRetryTechAbility={() => fetchTechAbility(houseFiasId)}
                />
            )}
        </SectionContainerLeadPage>
    );
};
